import { URLParamKey } from 'constants/urlParams'
import { TEST_TYPE_URL_DEFAULTS } from 'constants/testTypeUrlParams'
import { TestType } from 'types/testType'

const DEFAULT_TEST_TYPE = TestType.FOB

/**
 * Gets a URL search parameter value with case-insensitive key matching.
 */
export const getParamCaseInsensitive = (
  queryParams: URLSearchParams,
  targetKey: string
): string | null => {
  const normalizedTarget = targetKey.toLowerCase()

  for (const [key, value] of queryParams.entries()) {
    if (key.toLowerCase() === normalizedTarget) {
      return value
    }
  }

  return null
}

const validateTestType = (value: string | null): TestType => {
  if (!value) {
    return DEFAULT_TEST_TYPE
  }

  const normalized = value.toLowerCase().trim()
  const testTypeValues = Object.values(TestType) as Array<string>

  if (testTypeValues.includes(normalized)) {
    return normalized as TestType
  }

  return DEFAULT_TEST_TYPE
}

/**
 * Resolves the active test type from a URL search string.
 */
export const resolveTestTypeFromSearch = (search: string): TestType => {
  const queryParams = new URLSearchParams(search)
  const testTypeParam = getParamCaseInsensitive(queryParams, URLParamKey.TEST_TYPE)
  return validateTestType(testTypeParam)
}

/**
 * Removes sessionId and token from a URL search string (case-insensitive keys).
 * Collects keys first, then deletes — URLSearchParams.keys() must not be mutated mid-iteration.
 * @returns `?...` when other params remain, or `""` when the search is empty after stripping.
 */
export const stripSessionParamsFromSearch = (search: string): string => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const keysToDelete: Array<string> = []

  for (const key of params.keys()) {
    const normalized = key.toLowerCase()
    if (
      normalized === URLParamKey.SESSION_ID.toLowerCase() ||
      normalized === URLParamKey.TOKEN.toLowerCase()
    ) {
      keysToDelete.push(key)
    }
  }

  keysToDelete.forEach((key) => {
    params.delete(key)
  })

  const serialized = params.toString()
  return serialized ? `?${serialized}` : ''
}

/**
 * Ensures test-type-specific URL params are present (e.g. testType=fob|hpylori).
 * Preserves existing params such as sessionId and TestLabCamera.
 */
export const buildTestSearchString = (search: string): string => {
  const queryParams = new URLSearchParams(search)
  const testType = resolveTestTypeFromSearch(search)
  const defaults = TEST_TYPE_URL_DEFAULTS[testType]

  Object.entries(defaults).forEach(([key, value]) => {
    if (!getParamCaseInsensitive(queryParams, key)) {
      queryParams.set(key, value)
    }
  })

  // Normalize testType to canonical key/value even when provided with different casing
  queryParams.set(URLParamKey.TEST_TYPE, testType)

  const serialized = queryParams.toString()
  return serialized ? `?${serialized}` : ''
}
